import * as colibrioCoreBase from '@colibrio/colibrio-reader-framework/colibrio-core-base';
import * as colibrioCoreLocator from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {ILocator} from '@colibrio/colibrio-reader-framework/colibrio-core-locator';
import {
    ContentPositionTimelineUnit,
    IContentLocation,
    IContentPositionTimeline,
    IReaderDocument,
    IReaderPublication,
    IUnresolvedContentLocation,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-base';
import {
    EpubContentPositionTimelineOptions,
} from '@colibrio/colibrio-reader-framework/colibrio-readingsystem-formatadapter-epub';

import {VanillaReaderEventBus} from '../VanillaReaderEventBus/VanillaReaderEventBus';
import {
    IVanillaReaderContentPositionData,
    IVanillaReaderReadingProgressionTimeline,
    IVanillaReaderReadingProgressionTimelineData,
    IVanillaReaderReadingProgressionTimelinePositionData,
    VanillaProgressEventCallback,
    VanillaTimelineReadyEventCallback,
} from './VanillaReaderModel';
import {VanillaReaderPublication} from './VanillaReaderPublication';
import {VanillaReaderUtils} from './VanillaReaderUtils';
import {VanillaReaderView} from './VanillaReaderView';

/*
* # VanillaReaderProgressionTimeline
*
* ## RESPONSIBILITIES
*
* This class is a high level abstraction atop the Colibrio Reader Framework's `IContentPositionTimeline` or IPageProgressionTimeline.
* It sets up relevant events and exposes methods to add event callbacks.
*
* ## PRIMARY COLIBRIO FRAMEWORK TYPES
*
* - IReaderPublication
* - IContentPositionTimeline
* - IPageProgressionTimeline
*
* ## NOTES:
*
* For publications that are very long or very complex, this it can take a while to build the timeline.
* To make it possible for you to design a good user experience around this, the constructor takes in a progress callback
* which is called continuously with a number between 0 and 1 indication the current state of the creation process.
*
* */

export class VanillaReaderProgressionTimeline implements IVanillaReaderReadingProgressionTimeline {
    private _colibrioTimelineInstance!: IContentPositionTimeline;
    private _colibrioReaderPublication: IReaderPublication;
    private _timelineReadyCallback: VanillaTimelineReadyEventCallback | undefined = undefined;
    private _timelinePositionUpdatedCallback: VanillaProgressEventCallback | undefined = undefined;
    private _timelineData: IVanillaReaderReadingProgressionTimelineData | null = null;

    private constructor(
        private _vanillaReaderView: VanillaReaderView,
        private _vanillaPublication: VanillaReaderPublication,
        private _initialState: string | undefined = undefined,
        private _buildProgressCallback: (progress: number) => void,
    ) {
        this._colibrioReaderPublication = this._vanillaPublication.getColibrioReaderPublication();
    }

    static async create(
        vanillaReaderView: VanillaReaderView,
        vanillaPublication: VanillaReaderPublication,
        initialState: string | undefined = undefined,
        buildProgressCallback: (progress: number) => void,
    ): Promise<VanillaReaderProgressionTimeline> {
        const instance = new VanillaReaderProgressionTimeline(vanillaReaderView, vanillaPublication, initialState, buildProgressCallback);
        return instance._setup().catch(console.warn).then(() => instance);
    }

    private async _setup(): Promise<void> {
        this._vanillaReaderView.setVanillaReaderViewOptions({
            colibrioReaderViewOptions: {
                pageProgressionTimelineOptions: {
                    enabled: false,
                },
            },
        });

        this._colibrioTimelineInstance = await this._buildTimeline(this._colibrioReaderPublication, this._initialState, this._buildProgressCallback);

        let documentContentLocations = await this._fetchDocumentPositionData(this._colibrioTimelineInstance);

        this._timelineData = {
            length: this._colibrioTimelineInstance.getLength(),
            unit: this._colibrioTimelineInstance.getUnit(),
            sections: documentContentLocations,
        };
        if (this._timelineReadyCallback) {
            this._timelineReadyCallback(this._timelineData);
        }

    }

    async serialize(): Promise<IVanillaReaderReadingProgressionTimelineData | undefined> {
        if (!this._colibrioTimelineInstance) {
            console.warn('VanillaReaderProgressionTimeline.serialize(), No content position timeline instance available. Have you ');
            return;
        }

        let documentContentLocations = await this._fetchDocumentPositionData(this._colibrioTimelineInstance);

        return {
            length: this._colibrioTimelineInstance.getLength(),
            unit: this._colibrioTimelineInstance.getUnit(),
            sections: documentContentLocations,
        };
    }

    onTimelineReady(callback: VanillaTimelineReadyEventCallback): void {
        this._timelineReadyCallback = callback;
        if (this._colibrioTimelineInstance && this._timelineData) {
            callback(this._timelineData);
        }
    }

    onTimelinePositionUpdated(callback: VanillaProgressEventCallback): void {
        this._timelinePositionUpdatedCallback = callback;
    }

    isReady(): boolean {
        return this._colibrioTimelineInstance !== null && this._colibrioTimelineInstance !== undefined;
    }

    async fetchContentLocation(timelinePosition: number): Promise<IContentLocation | undefined> {
        if (!this._colibrioTimelineInstance) {
            return undefined;
        }
        return this._colibrioTimelineInstance?.fetchContentLocation(timelinePosition);
    }

    async fetchTimelinePosition(location: colibrioCoreLocator.ILocator): Promise<number | undefined> {
        if (!this._colibrioTimelineInstance) {
            return undefined;
        }

        return this._colibrioTimelineInstance?.fetchTimelinePosition(location);
    }

    async fetchTimelineRange(location: colibrioCoreLocator.ILocator | colibrioCoreLocator.ILocatorData | IContentLocation | IUnresolvedContentLocation): Promise<colibrioCoreBase.IIntegerRange | undefined> {
        return this._colibrioTimelineInstance?.fetchTimelineRange(location);

    }

    async fetchContentLocationAsRange(start: number, end: number): Promise<IContentLocation | undefined> {
        return this._colibrioTimelineInstance.fetchContentLocationAsRange(start, end);
    }

    getLength(): number {
        return this._colibrioTimelineInstance.getLength() || 0;
    }

    updateTimelinePosition(locator: ILocator): void {

        const updateFn = async () => {
            if (this._colibrioTimelineInstance && locator) {
                let position = await this.fetchTimelinePosition(locator);
                if (this._timelinePositionUpdatedCallback && position !== undefined) {
                    this._timelinePositionUpdatedCallback(position);
                }
            }
        };

        if (!this._colibrioTimelineInstance) {
            VanillaReaderEventBus.addEventListener('TIMELINE_READY', updateFn, true);
        } else {
            updateFn().catch(console.error);
        }

    }

    getBackingTimelineImplementation(): IContentPositionTimeline {
        return this._colibrioTimelineInstance;
    }

    toSerializedData(): string | undefined {
        return this._colibrioTimelineInstance.toSerializedData();
    }

    /*
    *
    * PRIVATE METHODS
    *
    * */

    private async _fetchDocumentPositionData(timeline: IContentPositionTimeline): Promise<IVanillaReaderReadingProgressionTimelinePositionData[]> {
        let documents: IReaderDocument[] = this._colibrioReaderPublication.getSpine();
        let documentContentLocations: IVanillaReaderReadingProgressionTimelinePositionData[] = [];

        for (let doc of documents) {
            let contentLocation = await doc.fetchContentLocationForEntireDocument();
            let contentPositionData: IVanillaReaderContentPositionData | undefined = await this._vanillaReaderView.fetchContentPositionData(contentLocation);

            if (contentPositionData && contentPositionData.locator) {
                let startPosition = await timeline.fetchTimelinePosition(contentLocation.collapseToStart());
                let endPosition = await timeline.fetchTimelinePosition(contentLocation.collapseToEnd());
                documentContentLocations.push({
                    contentPositionData,
                    range: {start: startPosition, end: endPosition},
                });
            }
        }

        return documentContentLocations;
    }

    private async _buildTimeline(
        colibrioReaderPublication: IReaderPublication,
        state: string | undefined,
        callback: (progress: number) => void,
    ): Promise<IContentPositionTimeline> {

        let timelinePromise: Promise<IContentPositionTimeline>;

        if (VanillaReaderUtils.isEpubReaderPublication(colibrioReaderPublication)) {
            let timelineOptions: EpubContentPositionTimelineOptions;

            if (colibrioReaderPublication.getAvailableContentPositionTimelineUnits().includes(ContentPositionTimelineUnit.PAGES)) {
                timelineOptions = {
                    unit: ContentPositionTimelineUnit.PAGES,
                };
            } else {
                timelineOptions = {
                    unit: ContentPositionTimelineUnit.WORDS,
                };
            }

            timelineOptions.serializedData = state;

            timelinePromise = colibrioReaderPublication.createContentPositionTimeline(colibrioReaderPublication.getSpine(), timelineOptions, callback);

        } else if (VanillaReaderUtils.isPdfReaderPublication(colibrioReaderPublication)) {

            let timelineOptions: EpubContentPositionTimelineOptions = {
                unit: ContentPositionTimelineUnit.PAGES,
                serializedData: state,
            };
            timelinePromise = colibrioReaderPublication.createContentPositionTimeline(colibrioReaderPublication.getSpine(), timelineOptions, callback);

        } else {
            timelinePromise = Promise.reject('Not an EPUB or PDF');
        }

        return timelinePromise;
    }

}
